/**
 * StageGateBanner — single surface for "why is this stage blocked /
 * needs review" messaging. Shared across Stage 1 → 4 so the drafter
 * sees the same visual vocabulary whether they're stuck at upload,
 * pending a statement, or reviewing QC findings.
 *
 * status semantics:
 *   blocked — user CANNOT proceed. Red border + icon. Secondary
 *             action is intentionally NOT shown in this mode.
 *   warning — user CAN proceed but should review. Amber. Primary
 *             action fixes the issue; secondary action proceeds.
 *   clear   — everything is good. Green. Rendered sparingly; most
 *             call sites omit the banner entirely when clear.
 */
import React from 'react';
import { AlertTriangle, CheckCircle, XOctagon, Loader2 } from 'lucide-react';

type Status = 'blocked' | 'warning' | 'clear';

interface Action {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  /** Hover text, e.g. why the action is disabled. */
  title?: string;
}

interface Props {
  status: Status;
  /** Text, or markup when the message carries links. */
  message: React.ReactNode;
  primaryAction?: Action;
  /** Only shown on ``warning`` status. Ignored for blocked/clear so a
   * hard block never offers a "proceed anyway" escape hatch. */
  secondaryAction?: Action;
  testId?: string;
  className?: string;
}

export default function StageGateBanner({
  status, message, primaryAction, secondaryAction, testId, className,
}: Props) {
  const palette = (
    status === 'blocked'
      ? 'bg-red-50 border-red-200 text-red-900'
      : status === 'warning'
        ? 'bg-amber-50 border-amber-200 text-amber-900'
        : 'bg-green-50 border-green-200 text-green-900'
  );
  const Icon = (
    status === 'blocked'
      ? XOctagon
      : status === 'warning'
        ? AlertTriangle
        : CheckCircle
  );
  const iconTone = (
    status === 'blocked'
      ? 'text-red-600'
      : status === 'warning'
        ? 'text-amber-600'
        : 'text-green-600'
  );
  const showSecondary = status === 'warning' && !!secondaryAction;
  return (
    <div
      role={status === 'blocked' ? 'alert' : 'status'}
      data-testid={testId || `stage-gate-${status}`}
      className={
        'flex flex-col sm:flex-row sm:items-center gap-3 rounded-sm border px-3 py-2 text-sm '
        + palette + (className ? ' ' + className : '')
      }
    >
      <div className="flex items-start gap-2 flex-1 min-w-0">
        <Icon size={16} className={`${iconTone} mt-0.5 shrink-0`} />
        <span className="leading-snug">{message}</span>
      </div>
      {(primaryAction || showSecondary) && (
        <div className="flex items-center gap-2 flex-wrap">
          {primaryAction && (
            <button
              type="button"
              data-testid={`${testId || `stage-gate-${status}`}-primary`}
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled || primaryAction.busy}
              title={primaryAction.title}
              className={
                'rounded-sm px-3 py-1.5 text-xs font-medium transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 '
                + (status === 'blocked'
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : status === 'warning'
                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                    : 'bg-green-600 text-white hover:bg-green-700')
              }
            >
              {primaryAction.busy && <Loader2 size={12} className="animate-spin" />}
              {primaryAction.label}
            </button>
          )}
          {showSecondary && secondaryAction && (
            <button
              type="button"
              data-testid={`${testId || `stage-gate-${status}`}-secondary`}
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled || secondaryAction.busy}
              title={secondaryAction.title}
              className="rounded-sm px-3 py-1.5 text-xs font-medium border border-amber-300 bg-white text-amber-800 hover:bg-amber-50 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {secondaryAction.busy && <Loader2 size={12} className="animate-spin" />}
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
